import { ReactNode } from 'react';
import { Link, useLocation, type LinkProps } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { cn } from '../utils/className';

interface NavigationItemProps extends Omit<
  LinkProps,
  'to' | 'children' | 'title'
> {
  route: string;
  title: string | ReactNode;
  icon?: ReactNode;
}

export function NavigationItem({
  route,
  title,
  icon,
  className,
  ...rest
}: NavigationItemProps) {
  const location = useLocation();
  const isActive = location.pathname === route;
  const { prefersMobileLayout } = useResponsive();

  const baseClasses = prefersMobileLayout
    ? 'text-primary hover:bg-surface-primary-support px-4'
    : 'text-inverted hover:bg-white/10 px-6';

  const activeClasses = prefersMobileLayout
    ? 'text-focus bg-surface-primary-support'
    : 'text-inverted bg-white/10';

  return (
    <li>
      <Link
        to={route}
        className={cn(
          `relative flex h-12 items-center gap-3 py-3 no-underline
          transition-colors`,
          baseClasses,
          isActive && activeClasses,
          className
        )}
        {...rest}
      >
        {isActive && (
          <span
            className={cn(
              'absolute top-2 left-0 h-8 w-1 rounded-r-sm',
              prefersMobileLayout ? 'bg-focus' : 'bg-inverted'
            )}
          />
        )}
        {icon ? (
          <span
            className='flex h-4 w-4 shrink-0 items-center justify-center
              [&_path]:fill-current [&_svg]:fill-current'
          >
            {icon}
          </span>
        ) : null}

        <span className={cn('size-p2 flex-1')}>{title}</span>
      </Link>
    </li>
  );
}
